import { useQuery } from '@tanstack/react-query';
import { buildListingSlug } from '@/utils/listingSlug';
import { supabase } from '@/integrations/supabase/client';
import { Link } from '@/lib/router-compat';
import { useCurrencyConversion } from '@/hooks/useCurrencyConversion';
import { AspectAwareImage } from '@/components/ui/AspectAwareImage';
import { ImagePlaceholder } from '@/components/ui/ImagePlaceholder';
import { Calendar, Gauge, GasPump } from '@phosphor-icons/react';
import { hasValidPhoto, selectBestCardImage } from '@/utils/imageSelection';
import useEmblaCarousel from 'embla-carousel-react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useCallback, useState, useEffect } from 'react';

interface SimilarListingsProps {
  currentListingId: string;
  make: string;
  price: number;
  categoryId?: string;
}

export function SimilarListings({ currentListingId, make, price, categoryId }: SimilarListingsProps) {
  const { formatPrice } = useCurrencyConversion();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start', slidesToScroll: 1 });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  const { data: listings = [] } = useQuery({
    queryKey: ['similar-listings', currentListingId, make, price],
    queryFn: async () => {
      // Find similar by same make OR similar price range (±30%)
      const priceMin = Math.round(price * 0.7);
      const priceMax = Math.round(price * 1.3);

      const { data, error } = await supabase
        .from('listings_v2')
        .select('id, make, model, year, price, currency, mileage_value, mileage_unit, photos, attributes, status')
        .eq('status', 'active')
        .neq('id', currentListingId)
        .or(`make.eq.${make},and(price.gte.${priceMin},price.lte.${priceMax})`)
        .order('created_at', { ascending: false })
        .limit(8);

      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  if (listings.length === 0) return null;

  return (
    <div className="py-6 border-t border-border">
      <h3 className="text-lg font-semibold mb-4">Vehicule similare</h3>
      <div className="relative">
        {canScrollPrev && (
          <button
            onClick={() => emblaApi?.scrollPrev()}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-background/90 border rounded-full shadow-md hover:bg-muted"
          >
            <CaretLeft size={20} />
          </button>
        )}
        {canScrollNext && (
          <button
            onClick={() => emblaApi?.scrollNext()}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-background/90 border rounded-full shadow-md hover:bg-muted"
          >
            <CaretRight size={20} />
          </button>
        )}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-4">
            {listings.map((item) => {
              const attrs = (item.attributes as any) || {};
              const photo = selectBestCardImage(item.photos || [], item.photos?.[0]);
              const valid = hasValidPhoto(photo);

              return (
                <Link
                  key={item.id}
                  to={`/listing/${buildListingSlug({ id: item.id, make: item.make, model: item.model, year: item.year, location_city: (item as any).location_city })}`}
                  className="flex-none w-[200px] md:w-[260px] rounded-xl border bg-card overflow-hidden hover:shadow-card-hover transition-shadow"
                >
                  <div className="aspect-video relative overflow-hidden">
                    {valid ? (
                      <AspectAwareImage src={photo!} alt={`${item.make} ${item.model}`} width={260} height={146} />
                    ) : (
                      <ImagePlaceholder iconSize="md" />
                    )}
                  </div>
                  <div className="p-3 space-y-1.5">
                    <h4 className="font-semibold text-sm truncate">{item.make} {item.model}</h4>
                    <div className="text-lg font-semibold font-serif text-primary">
                      {formatPrice(item.price, (item.currency as any) || 'EUR')}
                    </div>
                    <div className="flex flex-wrap gap-1.5 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={12} /> {item.year}
                      </span>
                      {item.mileage_value != null && (
                        <span className="inline-flex items-center gap-1">
                          <Gauge size={12} /> {new Intl.NumberFormat('ro-RO').format(item.mileage_value)} {item.mileage_unit || 'km'}
                        </span>
                      )}
                      {attrs.fuel_type && (
                        <span className="inline-flex items-center gap-1">
                          <GasPump size={12} /> {attrs.fuel_type}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
